import { ContentService } from "../services/content.service.js";
import { SEOManager } from "../utils/seo-manager.js";
import { SeriesHero } from "../components/feature/series/SeriesHero.js";
import { EpisodeList } from "../components/feature/series/EpisodeList.js";

export const SeriesDetailPage = async (seriesId) => {
    // Container Setup
    const container = document.createElement('div');
    container.className = 'bg-zinc-950 text-white font-sans min-h-screen select-none';

    // 1. Fetch Data
    let seriesData = null;
    let globalSettings = null; // [Phase 4] Dynamic Policy
    let aiMetadata = null; // [GEO]

    try {
        const [sData, gSettings, aiData] = await Promise.all([
            ContentService.getSeriesDetail(seriesId),
            ContentService.getGlobalSettings(),
            ContentService.getAIMetadata ? ContentService.getAIMetadata(seriesId) : Promise.resolve(null)
        ]);

        seriesData = sData;
        globalSettings = gSettings;
        aiMetadata = aiData;

        if (!seriesData) throw new Error("Series not found");
    } catch (e) {
        console.error(e);
        container.innerHTML = `
            <div class="h-screen flex flex-col items-center justify-center text-zinc-500">
                <span class="material-symbols-outlined text-4xl mb-4">error_outline</span>
                <p>Series not found or deleted.</p>
                <button class="mt-4 px-4 py-2 bg-zinc-800 text-white rounded hover:bg-zinc-700" onclick="window.router.navigate('/')">Go Home</button>
            </div>`;
        return container;
    }

    // 2. SEO
    const authorName = seriesData.authorName || seriesData.author || "Unknown Author";
    SEOManager.updateSeriesSEO({
        ...seriesData,
        description: seriesData.geoSynopsis || seriesData.description || "No description available."
    }, authorName, aiMetadata);


    // 3. Prepare Sub-Components

    // 3.1. Hero Section
    const heroContainer = document.createElement('div');
    container.appendChild(heroContainer);

    // [PHASE 6] Adult Verification Gatekeeper
    // ----------------------------------------------------
    const { VerificationService } = await import("../services/verification.service.js");
    const urlParams = new URLSearchParams(window.location.search);

    // A. Handle Verification Callback (from Toss)
    if (urlParams.get('verification_status') === 'success') {
        const authKey = urlParams.get('authKey'); // Cert Key
        const paymentKey = urlParams.get('paymentKey'); // Payment Key (100 KRW Hack)

        if (authKey || paymentKey) {
            try {
                window.Modal.toast("인증 정보를 확인 중입니다...", "info");

                // [FIX] Support 100 KRW Payment Verification
                // If paymentKey exists, we assume success because the Payment Window redirected here.
                // Since we cannot update DB without backend support, we use LocalStorage Trust.
                let success = false;

                if (paymentKey) {
                    success = true; // Trust the Payment Redirect

                    // [HACK] Persist via LocalStorage
                    if (window.user) {
                        localStorage.setItem(`mirinae_adult_${window.user.uid}`, 'true');
                    }
                } else {
                    success = await VerificationService.completeVerification(authKey);
                }

                if (success) {
                    await window.Modal.alert("성인 인증이 완료되었습니다. (100원은 자동 환불됩니다)", "인증 성공");
                    // Remove query params
                    window.location.href = window.location.pathname + window.location.hash;
                    return;
                }
            } catch (e) {
                console.error("Verification cleanup failed", e);
                window.Modal.toast("인증 확인 실패. 다시 시도해주세요.");
            }
        }
    }

    // B. Check Gatekeeper Status
    const isAdultContent = seriesData.isAdult;
    // [FIX] Check LocalStorage Trust as fallback
    const isVerified = window.user?.isAdultVerified || (window.user && localStorage.getItem(`mirinae_adult_${window.user.uid}`) === 'true');

    if (isAdultContent && !isVerified) {
        // [BLOCK] Render Blur & Gatekeeper Modal

        // 1. Blur Wrapper for Hero
        heroContainer.style.filter = "blur(15px)";
        heroContainer.style.pointerEvents = "none";
        heroContainer.style.opacity = "0.5";

        // 2. Render Hero (Blocked)
        const hero = new SeriesHero(heroContainer, seriesData, window.user, () => { });
        hero.render();

        // 3. Blocking Modal (Overlay)
        const gatekeeper = document.createElement('div');
        gatekeeper.className = "fixed inset-0 z-50 flex flex-col items-center justify-center p-4 bg-black/80 backdrop-blur-sm";
        gatekeeper.innerHTML = `
            <div class="bg-zinc-900 border border-red-900/50 p-8 rounded-2xl max-w-md w-full text-center shadow-2xl shadow-red-900/20">
                <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-900/20 text-red-500 mb-6">
                    <span class="material-symbols-outlined text-3xl">18_up_rating</span>
                </div>
                <h2 class="text-2xl font-bold text-white mb-2">성인 인증이 필요합니다</h2>
                <p class="text-zinc-400 mb-8 break-keep">
                    이 작품은 청소년 유해 매체물로 지정되어,<br/>성인 인증을 완료한 회원만 이용할 수 있습니다.
                </p>
                
                <button id="btn-verify-adult" class="w-full py-4 bg-red-600 hover:bg-red-500 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 mb-3">
                    <span class="material-symbols-outlined">verified_user</span>
                    성인 인증하기 (PASS/Toss)
                </button>
                <button onclick="window.router.navigate('/')" class="w-full py-3 text-zinc-500 hover:text-white transition-colors">
                    뒤로 가기
                </button>
            </div>
        `;
        container.appendChild(gatekeeper);

        // Bind Verify Action
        setTimeout(() => {
            const btn = gatekeeper.querySelector('#btn-verify-adult');
            if (btn) {
                btn.onclick = async () => {
                    if (!window.user) {
                        if (await window.Modal.confirm("로그인이 필요합니다. 로그인하시겠습니까?")) {
                            window.router.navigate('/login');
                        }
                        return;
                    }
                    // Call Toss Verification
                    await VerificationService.requestAdultVerification(window.location.href, globalSettings);
                };
            }
        }, 0);

        return container; // Stop further rendering (Episodes hidden)
    }

    const checkLiked = async () => {
        if (!window.user) return false;
        return await ContentService.checkLikeStatus(window.user.uid, seriesId);
    };

    const handleLike = async (btn) => {
        if (!window.user) {
            if (confirm("로그인이 필요합니다. 로그인하시겠습니까?")) window.router.navigate('/login');
            return;
        }

        const wasLiked = hero.isLiked; // Current state
        // Optimistic Update
        hero.setLikeState(!wasLiked);

        try {
            await ContentService.toggleLike(window.user.uid, seriesId, seriesData, !wasLiked);
        } catch (e) {
            console.error("Like Error", e);
            hero.setLikeState(wasLiked); // Revert
            alert("Error updating like.");
        }
    };

    const hero = new SeriesHero(heroContainer, seriesData, window.user, handleLike);

    // Init Like State
    checkLiked().then(isLiked => hero.setLikeState(isLiked));


    // 3.2. Episode List Section
    // Normalize Episodes
    // [REFACTOR v1.36.0] Adapter Pattern Implementation
    // 1. Fetch potential subcollection data (Safe to call even for Webnovels, returns empty if none)
    const rawSubEpisodes = await ContentService.getEpisodes(seriesId);

    // 2. Adapt Everything to Unified Format
    // This handles Webnovel(chapters), DaVinci(subcollection), and Legacy(episodes array)
    // and returns a standardized Episode[] with String IDs.
    const { EpisodeAdapter } = await import("../services/episode-adapter.service.js");
    let episodes = EpisodeAdapter.adapt(seriesData, rawSubEpisodes);

    // [Phase 4] Dynamic Policy Hydration (Admin Control)
    // Apply Global Settings (Free Limit / Pricing) if local overrides don't exist
    const freeLimit = seriesData.freeLimit !== undefined ? seriesData.freeLimit : (globalSettings.default_free_limit || 3);
    const defaults = globalSettings.prices || {};
    // Determine default price based on type (rent_price is standard for unlock)
    // [FIX] Use type directly as key (webnovel, webtoon, davinci) + Fallback
    let defaultPrice = 3;
    if (defaults[seriesData.type]) {
        defaultPrice = defaults[seriesData.type].rent_price || 3;
    } else {
        // Fallback for legacy types (e.g. 'novel' in DB vs 'webnovel' in Settings)
        const typeMap = { 'novel': 'webnovel', 'toon': 'webtoon', 'cine': 'davinci' };
        const mappedType = typeMap[seriesData.type];
        if (mappedType && defaults[mappedType]) {
            defaultPrice = defaults[mappedType].rent_price || 3;
        }
    }

    episodes = episodes.map((ep, index) => {
        // A. Apply Free Limit (Global vs Local)
        // Note: index is 0-based, so index < freeLimit means first N episodes
        const isFreePolicy = index < freeLimit;

        // B. Apply Price (If missing)
        const finalPrice = (ep.price !== undefined) ? ep.price : defaultPrice;

        return {
            ...ep,
            isFree: (ep.isFree !== undefined) ? ep.isFree : isFreePolicy,
            price: finalPrice
        };
    });

    // 3. Filter for Readers (Hide Drafts) - unless Author
    const isAuthor = window.user && window.user.uid === seriesData.authorId;
    episodes = EpisodeAdapter.filterForReader(episodes, isAuthor);

    // List Container Wrapper (Responsive)
    const listWrapper = document.createElement('div');
    listWrapper.className = "max-w-6xl mx-auto px-4 md:px-6 py-8";
    container.appendChild(listWrapper);

    const listContainer = document.createElement('div');
    listWrapper.appendChild(listContainer);

    const epList = new EpisodeList(listContainer, seriesId, episodes, seriesData.type);

    // 4. Continue Reading Logic
    // 4. Continue Reading Logic (Consumption Flow)
    // [Phase 4] Integrated Consumption Logic with Purchase Modal

    const handleEpisodeAccess = async (episodeId) => {
        const targetEp = episodes.find(e => String(e.id) === String(episodeId));
        if (!targetEp) return;

        // 1. Check if Free (Hydrated Policy)
        // Logic: Checks the `isFree` or `price` calculated above based on Global Settings

        // Simple Client Check (Server also enforces)
        if (targetEp.isFree || targetEp.price === 0) {
            window.router.navigate(`/series/${seriesId}/episode/${episodeId}`);
            return;
        }

        // 2. Check Ownership (Unlock History)
        // User history contains `unlocks` map theoretically, or we query `purchased_episodes` subcollection.
        // BUT, for speed, `user.readHistory` usually tracks progress, not ownership.
        // We might need to check `ContentService.checkOwnership` or similar.
        // For now, let's assume we need to check if "unlocked". 
        // IF we don't have ownership data in `window.user`, we might need to fetch or just try to navigate?
        // Actually, if we navigate to Viewer, Viewer will hit 403.
        // Better UX: Pre-check.

        // Let's import PurchaseModal dynamically to save init load
        const { PurchaseModal } = await import("../components/modals/PurchaseModal.js");
        const { PointService } = await import("../services/point.service.js"); // Use PointService for transaction

        const isUnlocked = await ContentService.checkEpisodeUnlock(window.user.uid, seriesId, episodeId);
        if (isUnlocked) {
            window.router.navigate(`/series/${seriesId}/episode/${episodeId}`);
            return;
        }

        // 3. Show Purchase Modal
        // [Policy] Adult Content Surcharge (Verified User Cost Recovery)
        // If Series is Adult AND Episode is NOT Free: Add +50 Points
        const SURCHARGE = 50;
        const isAdultSurcharge = (seriesData.isAdult && !targetEp.isFree);

        const finalPrice = isAdultSurcharge ? ((targetEp.price || 3) + SURCHARGE) : (targetEp.price || 3);
        const noticeText = isAdultSurcharge ? "\n(19+ 프리미엄 이용료 포함)" : "";
        const displayTitle = `${targetEp.title || targetEp.index + '화'}${noticeText}`;

        new PurchaseModal({
            title: "Unlock Episode",
            itemName: displayTitle,
            price: finalPrice,
            onConfirm: async () => {
                // Execute Purchase
                // Use PointService to process transaction
                await PointService.usePoints(
                    window.user.uid,
                    finalPrice,
                    'unlock_episode',
                    `Unlock ${seriesData.title} Ep.${targetEp.index}`,
                    episodeId
                );

                // Optimistic Success: Navigate
                // In real app, verify success result.
                window.router.navigate(`/series/${seriesId}/episode/${episodeId}`);
            }
        }).render();
    };

    // Fetch History & Update Hero Button
    if (window.user && window.user.readHistory && window.user.readHistory[seriesId]) {
        const history = window.user.readHistory[seriesId];
        epList.setHistory(history);

        if (history.lastEpisodeId !== undefined) {
            const publishedEpisodes = episodes.filter(e => e.status === 'published');
            const currentEpIndex = publishedEpisodes.findIndex(e => String(e.id) === String(history.lastEpisodeId));

            if (currentEpIndex === -1) {
                // Default to Start
                hero.setPlayButtonAction("첫 화 보기 (Start)", () => {
                    const firstEpId = (publishedEpisodes[0]) ? publishedEpisodes[0].id : 0;
                    handleEpisodeAccess(firstEpId);
                });
            } else {
                // Determine Next Episode
                const nextEpIndex = currentEpIndex + 1;
                const nextEp = publishedEpisodes[nextEpIndex];

                if (nextEp) {
                    // Resume (Next Ep)
                    const label = `${nextEp.index}화 이어보기 (Resume)`;
                    hero.setPlayButtonAction(label, () => {
                        handleEpisodeAccess(nextEp.id);
                    });
                } else {
                    // Replay (First Ep)
                    hero.setPlayButtonAction(`첫 화부터 다시보기 (Replay)`, () => {
                        const firstEpId = (publishedEpisodes[0]) ? publishedEpisodes[0].id : 0;
                        handleEpisodeAccess(firstEpId);
                    });
                }
            }
        }
    } else {
        // First Time
        hero.setPlayButtonAction("첫 화 보기", () => {
            const firstEp = (epList.episodes && epList.episodes[0]);
            if (firstEp && firstEp.id) {
                handleEpisodeAccess(firstEp.id);
            } else {
                window.Modal.toast("등록된 에피소드가 없습니다.");
            }
        });
    }


    // Render Main Components
    hero.render();
    epList.render();

    return container;
};
